import React from 'react';
import {
  BookIdentityReadiness,
  PageCompositionStrategy,
  PageCompositionTreatment,
  QuickCreateComposition,
  Storybook,
  QuickCreateStage,
  QuickCreateState
} from '../types/storybook';
import {
  PAGE_COMPOSITION_STRATEGIES,
  QUICK_CREATE_CONTROL_OPTIONS
} from '@tiny-fox/storybook-layout';
import {
  formatQuickCreateDuration,
  isQuickCreateStageUnlocked,
  QUICK_CREATE_STAGE_LABELS,
  QUICK_CREATE_STAGE_ORDER
} from '../utils/quickCreate';
import ReferenceBookSelector from './ReferenceBookSelector';
import {
  buildQuickCreateStoryboard,
  clearQuickCreatePageOverride,
  resolveQuickCreateComposition,
  treatmentsForPageStrategy,
  updateQuickCreatePageOverride
} from '../utils/quickCreateComposition';
import './QuickCreateWorkflow.css';

export interface QuickCreateWorkflowProps {
  storybook: Storybook;
  state: QuickCreateState;
  availableTokens?: number;
  preflight?: { errors: number; warnings: number; checked: boolean };
  identityReadiness?: BookIdentityReadiness;
  busyAction?: string | null;
  error?: string | null;
  onSelectStage: (stage: QuickCreateStage) => void;
  onApproveBrief: () => void | Promise<void>;
  onGenerateOutline: () => void | Promise<void>;
  onApproveOutline: () => void | Promise<void>;
  onApproveCastStyle: () => void | Promise<void>;
  onPrepareIdentity?: () => void | Promise<void>;
  onApproveIdentity?: () => void | Promise<void>;
  onUpdateComposition?: (composition: QuickCreateComposition) => void | Promise<void>;
  resolveImageUrl?: (url: string) => string;
  onAnalyzeReferenceBook: (bookName: string) => void | Promise<void>;
  onUseSeriesTemplate: (templateId: 'tiny-little-fox-sequel-v1') => void | Promise<void>;
  onClearReferenceBook: () => void | Promise<void>;
  onGenerateDraft: () => void | Promise<void>;
  onRunReview: () => void | Promise<void>;
  onApproveReview: () => void | Promise<void>;
  onExport: () => void | Promise<void>;
  onAdvancedEditor: () => void;
  onClose: () => void;
  draftJobPanel?: React.ReactNode;
}

const approvalLabel = (state: QuickCreateState, stage: QuickCreateStage) => {
  if (stage === 'export') return state.status === 'completed' ? 'PDF ready' : undefined;
  const approval = state.approvals[stage];
  return approval?.approved ? `Approved · revision ${approval.revision}` : undefined;
};

const QuickCreateWorkflow: React.FC<QuickCreateWorkflowProps> = ({
  storybook,
  state,
  availableTokens,
  preflight = { errors: 0, warnings: 0, checked: false },
  identityReadiness = { ready: true, issues: [], characters: [] },
  busyAction,
  error,
  onSelectStage,
  onApproveBrief,
  onGenerateOutline,
  onApproveOutline,
  onApproveCastStyle,
  onPrepareIdentity = onApproveCastStyle,
  onApproveIdentity = onApproveCastStyle,
  onUpdateComposition = () => undefined,
  resolveImageUrl = value => value,
  onAnalyzeReferenceBook,
  onUseSeriesTemplate,
  onClearReferenceBook,
  onGenerateDraft,
  onRunReview,
  onApproveReview,
  onExport,
  onAdvancedEditor,
  onClose,
  draftJobPanel
}) => {
  const outline = storybook.structuredPlotData;
  const estimate = state.estimate;
  const insufficientTokens = typeof availableTokens === 'number'
    && Boolean(estimate)
    && availableTokens < (estimate?.tokens || 0);
  const isBusy = Boolean(busyAction);
  const outlineCast = (outline?.characters || []).map(outlineCharacter => (
    storybook.characters.find(character => character.name.trim().toLocaleLowerCase()
      === outlineCharacter.name.trim().toLocaleLowerCase())
  )).filter((character): character is Storybook['characters'][number] => Boolean(character));
  const allIdentityKitsBuilt = outlineCast.length > 0 && outlineCast.every(character => (
    character.referenceVersions?.some(version => version.references.length === 4)
  ));
  const hasPendingIdentityKits = allIdentityKitsBuilt && outlineCast.some(character => (
    character.referenceVersions?.some(version => version.status === 'draft' && version.references.length === 4)
  ));
  const composition = resolveQuickCreateComposition(state.composition);
  const storyboard = React.useMemo(
    () => buildQuickCreateStoryboard(outline, composition),
    [composition, outline]
  );
  const formatChoice = (value: string) => value
    .split('-')
    .map(word => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ');
  const persistComposition = (next: QuickCreateComposition) => {
    if (!isBusy) {
      const nextStoryboard = buildQuickCreateStoryboard(outline, next);
      void onUpdateComposition({ ...next, storyboardHash: nextStoryboard.storyboardHash });
    }
  };

  const primary = (() => {
    switch (state.stage) {
      case 'brief':
        return { label: 'Approve brief & continue', action: onApproveBrief, disabled: false };
      case 'outline':
        return outline
          ? { label: 'Approve outline', action: onApproveOutline, disabled: false }
          : { label: 'Generate outline', action: onGenerateOutline, disabled: false };
      case 'cast-style':
        return identityReadiness.ready
          ? { label: 'Approve cast & style', action: onApproveCastStyle, disabled: !outline }
          : hasPendingIdentityKits
            ? { label: 'Approve shown identity kits', action: onApproveIdentity, disabled: !outline }
            : { label: 'Build identity kits', action: onPrepareIdentity, disabled: !outline };
      case 'draft':
        return {
          label: ['failed', 'review-required', 'cancelled'].includes(state.draft?.status || '')
            ? 'Retry unfinished pages'
            : 'Create full first draft',
          action: onGenerateDraft,
          disabled: !outline || insufficientTokens || ['queued', 'running', 'paused'].includes(state.draft?.status || '')
        };
      case 'review':
        return preflight.checked
          ? {
            label: preflight.errors ? 'Fix blockers in editor' : 'Approve book for export',
            action: preflight.errors ? onAdvancedEditor : onApproveReview,
            disabled: false
          }
          : { label: 'Run book layout check', action: onRunReview, disabled: false };
      case 'export':
        return {
          label: state.status === 'completed' ? 'Download another PDF' : 'Create print-ready PDF',
          action: onExport,
          disabled: !state.approvals.review?.approved
        };
      default:
        return { label: 'Continue', action: onAdvancedEditor, disabled: false };
    }
  })();
  const referenceSelector = (
    <ReferenceBookSelector
      currentBook={storybook.useReferenceBook ? storybook.referenceBook : undefined}
      currentTemplateId={storybook.seriesTemplateId || undefined}
      storyStylePrompt={storybook.storyStylePrompt}
      visualStylePrompt={storybook.visualStylePrompt}
      busy={busyAction === 'Analyzing reference book…'
        || busyAction === 'Clearing reference book…'
        || busyAction === 'Applying measured sequel template…'}
      disabled={isBusy}
      onAnalyze={onAnalyzeReferenceBook}
      onUseSeriesTemplate={onUseSeriesTemplate}
      onClear={onClearReferenceBook}
    />
  );

  return (
    <div className="quick-create-backdrop" role="presentation">
      <section
        className="quick-create-workflow"
        data-testid="quick-create-workflow"
        aria-label="Quick Create workflow"
        aria-modal="true"
        role="dialog"
      >
        <header className="quick-create-header">
          <div>
            <p className="quick-create-eyebrow">Quick Create</p>
            <h2>{storybook.title}</h2>
            <p>Your progress and approvals are saved after every stage.</p>
          </div>
          <button className="quick-create-close" type="button" onClick={onClose} aria-label="Close Quick Create">×</button>
        </header>

        <nav className="quick-create-stages" aria-label="Book creation stages">
          {QUICK_CREATE_STAGE_ORDER.map((stage, index) => {
            const unlocked = isQuickCreateStageUnlocked(state, stage);
            const current = state.stage === stage;
            const approval = approvalLabel(state, stage);
            return (
              <button
                type="button"
                key={stage}
                className={`quick-create-stage${current ? ' current' : ''}${approval ? ' approved' : ''}`}
                data-stage={stage}
                aria-current={current ? 'step' : undefined}
                disabled={!unlocked || isBusy}
                onClick={() => onSelectStage(stage)}
              >
                <span className="quick-create-stage-number" aria-hidden="true">{approval ? '✓' : index + 1}</span>
                <span>
                  <strong>{QUICK_CREATE_STAGE_LABELS[stage]}</strong>
                  <small>{approval || (current ? 'In progress' : unlocked ? 'Ready' : 'Locked')}</small>
                </span>
              </button>
            );
          })}
        </nav>

        <main className="quick-create-content">
          {state.stage === 'brief' && (
            <div className="quick-create-section">
              <p className="quick-create-kicker">1 · Brief</p>
              <h3>Confirm the audience and shape of the book</h3>
              <blockquote>{state.brief.premise}</blockquote>
              <dl className="quick-create-facts">
                <div><dt>Reading age</dt><dd>{state.brief.readingAge}</dd></div>
                <div><dt>Story pages</dt><dd>{state.brief.pageCount}</dd></div>
                <div><dt>Trim</dt><dd>{state.brief.canvasSize}</dd></div>
                <div><dt>Tone</dt><dd>{state.brief.storyTone}</dd></div>
              </dl>
              <h4>Series and reference direction</h4>
              <p>Choose this before writing the outline so the prose rhythm, trim, and illustration plan are generated together.</p>
              {referenceSelector}
            </div>
          )}

          {state.stage === 'outline' && (
            <div className="quick-create-section">
              <p className="quick-create-kicker">2 · Outline</p>
              <h3>{outline ? 'Review the complete story plan' : 'Turn your brief into page-by-page beats'}</h3>
              {outline ? (
                <>
                  <div className="quick-create-summary-grid">
                    <div><strong>{outline.pages.length}</strong><span>story pages</span></div>
                    <div><strong>{outline.characters.length}</strong><span>characters</span></div>
                    <div><strong>{outline.backgrounds.length}</strong><span>settings</span></div>
                  </div>
                  <ol className="quick-create-outline-list">
                    {outline.pages.slice(0, 6).map((page, index) => (
                      <li key={page.outlinePageId || index}>
                        <strong>Page {index + 1}</strong>
                        <span>{page.textBoxes?.map(box => box.content).join(' ') || page.scenePrompt || 'Planned scene'}</span>
                      </li>
                    ))}
                  </ol>
                  {outline.pages.length > 6 && <p className="quick-create-muted">Plus {outline.pages.length - 6} more planned pages.</p>}
                </>
              ) : (
                <p>Quick Create will write age-appropriate copy and plan reusable characters and settings. You approve the plan before any expensive illustrations begin.</p>
              )}
            </div>
          )}

          {state.stage === 'cast-style' && (
            <div className="quick-create-section">
              <p className="quick-create-kicker">3 · Cast & Style</p>
              <h3>Keep the visual direction stable before drafting</h3>
              <div className="quick-create-two-column">
                <div>
                  <h4>Cast</h4>
                  <ul>{outline?.characters.map(character => <li key={character.name}><strong>{character.name}</strong><span>{character.description}</span></li>)}</ul>
                </div>
                <div>
                  <h4>Book style</h4>
                  <p>{storybook.drawingType || 'Digital art'} · {storybook.drawingStyle || 'Children’s book illustration'}</p>
                  <p>{storybook.visualStylePrompt || `${state.brief.storyTone} visual treatment with a consistent palette and simple text-safe compositions.`}</p>
                </div>
              </div>
              {referenceSelector}
              {outline && (
                <section className="quick-create-composition" data-testid="quick-create-composition">
                  <header>
                    <div>
                      <h4>Page composition storyboard</h4>
                      <p>Plan the reading rhythm before any paid page illustration is generated.</p>
                    </div>
                    <span data-testid="quick-create-storyboard-provider-calls">
                      {storyboard.providerCallsRequired} AI calls
                    </span>
                  </header>
                  <div className="quick-create-composition-controls">
                    <label>
                      <span>Book rhythm</span>
                      <select
                        data-testid="quick-create-composition-mode"
                        value={composition.mode}
                        disabled={isBusy}
                        onChange={event => persistComposition({
                          ...composition,
                          mode: event.target.value as QuickCreateComposition['mode']
                        })}
                      >
                        {QUICK_CREATE_CONTROL_OPTIONS.composition.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>Text density</span>
                      <select
                        data-testid="quick-create-text-density"
                        value={composition.textDensity}
                        disabled={isBusy}
                        onChange={event => persistComposition({
                          ...composition,
                          textDensity: event.target.value as QuickCreateComposition['textDensity']
                        })}
                      >
                        {QUICK_CREATE_CONTROL_OPTIONS.density.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>Reading surface</span>
                      <select
                        data-testid="quick-create-treatment"
                        value={composition.treatment}
                        disabled={isBusy}
                        onChange={event => persistComposition({
                          ...composition,
                          treatment: event.target.value as QuickCreateComposition['treatment']
                        })}
                      >
                        {QUICK_CREATE_CONTROL_OPTIONS.treatment.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <div className="quick-create-storyboard" aria-label="Whole-book page composition storyboard">
                    {storyboard.pages.map(page => {
                      const source = outline.pages[page.pageIndex];
                      const hasOverride = Boolean(composition.pageOverrides[page.pageId]);
                      const availableStrategies = (PAGE_COMPOSITION_STRATEGIES as readonly PageCompositionStrategy[])
                        .filter(strategy => !page.hasText || strategy !== 'image-only');
                      const availableTreatments = treatmentsForPageStrategy(page.requestedStrategy);
                      const copy = (source?.textBoxes || []).map(box => box.content).join(' ');
                      return (
                        <article
                          className={`quick-create-storyboard-card strategy-${page.strategy}`}
                          data-testid={`quick-create-storyboard-page-${page.pageId}`}
                          key={page.pageId}
                        >
                          <div className="quick-create-storyboard-card-heading">
                            <strong>Page {page.pageIndex + 1}</strong>
                            <small>{page.totalWords} words</small>
                          </div>
                          <div className="quick-create-storyboard-diagram" aria-hidden="true">
                            <span className="storyboard-art-region" />
                            <span className="storyboard-panel-region" />
                            <span className="storyboard-text-lines"><i /><i /><i /></span>
                          </div>
                          <p title={copy}>{copy || 'Wordless illustration beat'}</p>
                          <dl>
                            <div><dt>Safe preview</dt><dd>{formatChoice(page.strategy)}</dd></div>
                            <div><dt>Treatment</dt><dd>{formatChoice(page.treatment)}</dd></div>
                          </dl>
                          {page.status === 'requires-image-analysis' && (
                            <small className="quick-create-storyboard-guard">
                              Quiet zone requested; protected panel used until this exact image passes analysis.
                            </small>
                          )}
                          <div className="quick-create-page-override">
                            <label>
                              <span>Page strategy</span>
                              <select
                                aria-label={`Page ${page.pageIndex + 1} strategy`}
                                value={page.requestedStrategy}
                                disabled={isBusy}
                                onChange={event => persistComposition(updateQuickCreatePageOverride(
                                  outline,
                                  composition,
                                  page.pageId,
                                  event.target.value as PageCompositionStrategy
                                ))}
                              >
                                {availableStrategies.map(strategy => (
                                  <option key={strategy} value={strategy}>{formatChoice(strategy)}</option>
                                ))}
                              </select>
                            </label>
                            <label>
                              <span>Page treatment</span>
                              <select
                                aria-label={`Page ${page.pageIndex + 1} treatment`}
                                value={page.requestedTreatment}
                                disabled={isBusy || availableTreatments.length === 1}
                                onChange={event => persistComposition(updateQuickCreatePageOverride(
                                  outline,
                                  composition,
                                  page.pageId,
                                  page.requestedStrategy,
                                  event.target.value as PageCompositionTreatment
                                ))}
                              >
                                {availableTreatments.map(treatment => (
                                  <option key={treatment} value={treatment}>{formatChoice(treatment)}</option>
                                ))}
                              </select>
                            </label>
                            <button
                              type="button"
                              disabled={isBusy || !hasOverride}
                              onClick={() => persistComposition(clearQuickCreatePageOverride(composition, page.pageId))}
                            >
                              Use book rhythm
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                  <p className="quick-create-muted">
                    Story text and approved art stay unchanged. Overrides only change deterministic page composition.
                  </p>
                </section>
              )}
              <div className={`quick-create-draft-status ${identityReadiness.ready ? 'complete' : 'review-required'}`}>
                <strong>{identityReadiness.ready ? 'Identity locked' : 'Identity setup required'}</strong>
                <span>
                  {identityReadiness.ready
                    ? 'The Art Bible, model profile, and every four-view character kit are approved.'
                    : `${identityReadiness.issues.length} ${identityReadiness.issues.length === 1 ? 'item' : 'items'} must be completed before page generation.`}
                </span>
                {!identityReadiness.ready && identityReadiness.issues.slice(0, 4).map((issue, index) => (
                  <small key={`${issue.code}:${issue.characterId || index}`}>{issue.message}</small>
                ))}
              </div>
              {hasPendingIdentityKits && (
                <div className="quick-create-identity-previews" aria-label="Generated character identity kit previews">
                  {storybook.characters.map(character => {
                    const kit = [...(character.referenceVersions || [])]
                      .reverse()
                      .find(version => version.references.length === 4);
                    if (!kit) return null;
                    return (
                      <section key={character.characterId || character.name}>
                        <h4>{character.name}</h4>
                        <div>
                          {kit.references.map(reference => (
                            <figure key={reference.id}>
                              <img src={resolveImageUrl(reference.thumbnailUrl || reference.imageUrl)} alt={`${character.name} ${reference.purpose} identity reference`} />
                              <figcaption>{reference.purpose}</figcaption>
                            </figure>
                          ))}
                        </div>
                      </section>
                    );
                  })}
                  <p className="quick-create-notice">Review every view. The next action is the explicit approval gate; no page can be drafted before you approve these exact versions.</p>
                </div>
              )}
              <p className="quick-create-muted">A reference guides both story and art. Changing it after an outline exists sends the workflow back to regenerate that outline with the new direction.</p>
              <p className="quick-create-notice">Approval freezes exact Art Bible, character-reference, and model-profile versions for every page task.</p>
            </div>
          )}

          {state.stage === 'draft' && (
            <div className="quick-create-section">
              <p className="quick-create-kicker">4 · Draft</p>
              <h3>Generate every planned page with protected text layouts</h3>
              <p>Characters and settings are reused, then each page is placed with deterministic templates so you do not have to arrange narration by hand.</p>
              {state.draft && state.draft.status !== 'not-started' && (
                <div className={`quick-create-draft-status ${state.draft.status}`}>
                  <strong>{state.draft.status.replace('-', ' ')}</strong>
                  <span>{state.draft.completedPages} complete · {state.draft.failedPages} need attention · {state.draft.totalPages} total</span>
                  {state.draft.error && <small>{state.draft.error}</small>}
                </div>
              )}
              {draftJobPanel}
            </div>
          )}

          {state.stage === 'review' && (
            <div className="quick-create-section">
              <p className="quick-create-kicker">5 · Review</p>
              <h3>Check the whole book before export</h3>
              <div className={`quick-create-review-status${preflight.checked ? ' checked' : ''}`}>
                <div><strong>{preflight.checked ? preflight.errors : '—'}</strong><span>blocking issues</span></div>
                <div><strong>{preflight.checked ? preflight.warnings : '—'}</strong><span>warnings</span></div>
              </div>
              <p>{preflight.checked
                ? preflight.errors
                  ? 'Open the Advanced Editor to apply the listed safe fixes or review the affected pages.'
                  : 'No blocking readability or layout problems were found. Warnings can be reviewed before approval.'
                : 'The layout check verifies overflow, margins, contrast, image resolution, fonts, and page geometry.'}</p>
            </div>
          )}

          {state.stage === 'export' && (
            <div className="quick-create-section">
              <p className="quick-create-kicker">6 · Export</p>
              <h3>{state.status === 'completed' ? 'Your book is ready' : 'Create the print-ready PDF'}</h3>
              <p>Export repeats the layout checks against the exact selected pages. The workflow is complete only after PDF generation succeeds.</p>
              <dl className="quick-create-facts">
                <div><dt>Book</dt><dd>{storybook.title}</dd></div>
                <div><dt>Format</dt><dd>{state.brief.canvasSize}</dd></div>
                <div><dt>Pages</dt><dd>{storybook.pages.length}</dd></div>
                <div><dt>Status</dt><dd>{state.status === 'completed' ? 'PDF created' : 'Ready to export'}</dd></div>
              </dl>
            </div>
          )}

          {estimate && ['brief', 'outline', 'cast-style', 'draft'].includes(state.stage) && (
            <aside className="quick-create-estimate" data-testid="quick-create-estimate">
              <div><span>Expected token cost</span><strong>{estimate.tokens}</strong></div>
              <div><span>Expected generation time</span><strong>{formatQuickCreateDuration(estimate.seconds)}</strong></div>
              <small>{estimate.basis === 'outline' ? 'Calculated from this outline and existing reusable art.' : 'Provisional estimate; recalculated after outline.'}</small>
              {typeof availableTokens === 'number' && <small>Available balance: {availableTokens} tokens.</small>}
              {insufficientTokens && <p role="alert">You need {estimate.tokens - (availableTokens || 0)} more tokens to start the full draft. Add tokens first so generation cannot stop halfway.</p>}
            </aside>
          )}

          {error && <div className="quick-create-error" role="alert">{error}</div>}
        </main>

        <footer className="quick-create-footer">
          <button type="button" className="quick-create-secondary" onClick={onAdvancedEditor}>Advanced Editor</button>
          <button
            type="button"
            className="quick-create-primary"
            data-testid="quick-create-primary-action"
            onClick={() => void primary.action()}
            disabled={isBusy || primary.disabled}
          >
            {busyAction || primary.label}
          </button>
        </footer>
      </section>
    </div>
  );
};

export default QuickCreateWorkflow;
